import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter, Redirect } from 'react-router-dom';
import {
    Grid,
    CircularProgress,
    MenuItem,
    Select,
    InputLabel,
    Fab,
    TextField,
    makeStyles,
    Button,
    FormHelperText,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { useDispatch, useSelector } from 'react-redux';
import MUIDataTable from 'mui-datatables';
import SideNav from '../../components/Admin/SideNav';
import Modal from '../../components/Modal/modal_cancel_save';
import { safeGet, safePost } from '../../axios-default';
import { easySnackbar } from '../../store/Noti/noti.actions';
import { useLang } from '../../util/lang';

const CURRENCIES = ['USD', 'EUR', 'CNY', 'HKD', 'SGD', 'TWD', 'AUD', 'GBP', 'NZD', 'MYR', 'THB'];

const useStyles = makeStyles((theme) => ({
    rootContainer: {
        padding: 16,
        margin: '8px 0px 0px 238px',
        [theme.breakpoints.between('xs', 'sm')]: {
            margin: '12px 0px 0px 0px',
            width: '100%',
        },
        width: 'calc(100% - 238px)',
    },
    filterGrid: {
        padding: 4,
    },
}));

const SelfDesignPricing = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { distr_groups } = useSelector((store) => store.res);
    const get = useLang('SelfDesignPricing');

    const [loading, setLoading] = useState(false);
    const [selectedDistrGroup, setSelectedDistrGroup] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState('HKD');
    const [pricingData, setPricingData] = useState([]);
    const [filteredDistrGroups, setFilteredDistrGroups] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editRow, setEditRow] = useState(null);
    const [editCost, setEditCost] = useState('');
    const [editModalError, setEditModalError] = useState('');
    const [savedCurrency, setSavedCurrency] = useState(null);
    const [savingCurrency, setSavingCurrency] = useState(false);

    // activeDistrGroupIds stored as state (not ref) so changes trigger re-render
    const [activeDistrGroupIds, setActiveDistrGroupIds] = useState(new Set());
    const [accessChecked, setAccessChecked] = useState(false);
    const [accessDenied, setAccessDenied] = useState(false);

    let role = null;
    let myDistrGroupId = null;
    try {
        role = JSON.parse(localStorage.getItem('role'));
    } catch (e) { console.error('Failed to parse role from localStorage:', e); }
    try {
        myDistrGroupId = JSON.parse(localStorage.getItem('distr_group_id'));
    } catch (e) { console.error('Failed to parse distr_group_id from localStorage:', e); }

    // Fetch active distributor group IDs from API on mount
    useEffect(() => {
        if (role !== 'DISTR') {
            setAccessChecked(true);
            setAccessDenied(true);
            return;
        }
        (async () => {
            try {
                const data = await safeGet('res/self-design-pricing/distr-groups');
                if (data.success && data.distr_groups) {
                    const ids = new Set(data.distr_groups.map((d) => d.distr_group_id));
                    setActiveDistrGroupIds(ids);
                    if (role === 'DISTR' && !ids.has(Number(myDistrGroupId))) {
                        setAccessDenied(true);
                    }
                }
            } catch (err) {
                console.error(err);
                dispatch(easySnackbar(get('failedToLoad'), 'error'));
            }
            setAccessChecked(true);
        })();
    }, []);

    // Filter Redux distr_groups whenever either data source updates
    useEffect(() => {
        if (activeDistrGroupIds.size > 0 && Object.keys(distr_groups).length > 0) {
            const filtered = Object.values(distr_groups)
                .filter((dg) => activeDistrGroupIds.has(Number(dg.distr_group_id)))
                .sort((a, b) => a.distr_group_name.localeCompare(b.distr_group_name));
            setFilteredDistrGroups(filtered);
        }
    }, [distr_groups, activeDistrGroupIds]);

    // Create dropdown options
    const distrGroupOptions = useMemo(() => {
        return filteredDistrGroups.map(({ distr_group_id, distr_group_name }) => (
            <MenuItem key={distr_group_id} value={distr_group_id}>
                {distr_group_name}
            </MenuItem>
        ));
    }, [filteredDistrGroups]);

    const currencyOptions = useMemo(() => {
        return CURRENCIES.map((c) => (
            <MenuItem key={c} value={c}>
                {c}
            </MenuItem>
        ));
    }, []);

    // Load pricing data when both selections are made
    const loadPricing = useCallback(async () => {
        if (!selectedDistrGroup || !selectedCurrency) return;

        setLoading(true);
        try {
            const data = await safeGet(
                `res/self-design-pricing?distr_group_id=${selectedDistrGroup}&currency=${selectedCurrency}`
            );
            if (data.success) {
                setPricingData(data.pricing || []);
            } else {
                dispatch(easySnackbar(data.message || get('failedToLoad'), 'error'));
            }
        } catch (err) {
            console.error(err);
            dispatch(easySnackbar(get('failedToLoad'), 'error'));
        }
        setLoading(false);
    }, [selectedDistrGroup, selectedCurrency, dispatch, get]);

    useEffect(() => {
        if (selectedDistrGroup && selectedCurrency) {
            loadPricing();
        }
    }, [selectedDistrGroup, selectedCurrency]);

    useEffect(() => {
        if (!selectedDistrGroup) {
            setSavedCurrency(null);
            return;
        }
        (async () => {
            try {
                const data = await safeGet(`res/self-design-pricing/${selectedDistrGroup}/currency`);
                if (data.success) {
                    setSavedCurrency(data.currency);
                }
            } catch (err) {
                console.error(err);
            }
        })();
    }, [selectedDistrGroup]);

    // Handle edit button click
    const handleEdit = useCallback((row) => {
        setEditRow(row);
        setEditCost(row.distributor_cost != null ? String(row.distributor_cost) : '');
        setEditModalError('');
        setEditModalOpen(true);
    }, []);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!editRow) return;

        if (editCost.trim() === '' || isNaN(Number(editCost)) || Number(editCost) < 0) {
            setEditModalError(get('invalidPrice'));
            return;
        }
        const costNum = Number(editCost);

        setLoading(true);
        try {
            const data = await safePost(`res/self-design-pricing/${editRow.id}/price`, {
                currency: selectedCurrency,
                price: costNum,
            });
            if (data.success) {
                dispatch(easySnackbar(get('priceUpdated'), 'success'));
                setEditModalOpen(false);
                await loadPricing();
            } else {
                dispatch(easySnackbar(data.message || get('failedToUpdate'), 'error'));
            }
        } catch (err) {
            console.error(err);
            dispatch(easySnackbar(get('failedToUpdate'), 'error'));
        }
        setLoading(false);
    }, [editRow, editCost, selectedCurrency, dispatch, loadPricing, get]);

    // Save the selected currency for the distributor group
    const handleSaveCurrency = useCallback(async () => {
        if (!selectedDistrGroup || !selectedCurrency) return;
        setSavingCurrency(true);
        try {
            const data = await safePost(`res/self-design-pricing/${selectedDistrGroup}/save-currency`, {
                currency: selectedCurrency,
            });
            if (data.success) {
                setSavedCurrency(selectedCurrency);
                dispatch(easySnackbar(get('currencySaved'), 'success'));
            } else {
                dispatch(easySnackbar(data.message || get('incompletePricingError'), 'error'));
            }
        } catch (err) {
            dispatch(easySnackbar(get('failedToSaveCurrency'), 'error'));
        }
        setSavingCurrency(false);
    }, [selectedDistrGroup, selectedCurrency, dispatch, get]);

    // Table columns
    const columns = useMemo(
        () => [
            {
                name: 'id',
                label: 'Id',
                options: {
                    filter: false,
                    sort: true,
                },
            },
            {
                name: 'product_code',
                label: get('productCode'),
                options: {
                    filter: true,
                    sort: true,
                },
            },
            {
                name: 'product_name',
                label: get('product'),
                options: {
                    filter: true,
                    sort: true,
                },
            },
            {
                name: 'distributor_cost',
                label: get('distributorCost'),
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        if (value == null) return <span style={{ color: '#999' }}>—</span>;
                        return Number(value).toFixed(2);
                    },
                },
            },
            {
                name: 'action',
                label: get('action'),
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: (_value, tableMeta) => {
                        const row = pricingData.find((r) => r.id === tableMeta.rowData[0]);
                        return (
                            <Fab
                                size="small"
                                aria-label="edit"
                                style={{
                                    backgroundColor: '#75a0de',
                                    color: 'white',
                                    margin: '0px 4px',
                                }}
                                onClick={() => handleEdit(row)}
                            >
                                <EditIcon />
                            </Fab>
                        );
                    },
                },
            },
        ],
        [pricingData, handleEdit, get]
    );

    // Get selected distributor group name for display in modal
    const selectedDistrGroupName = useMemo(() => {
        const found = filteredDistrGroups.find((dg) => String(dg.distr_group_id) === String(selectedDistrGroup));
        return found ? found.distr_group_name : '';
    }, [filteredDistrGroups, selectedDistrGroup]);

    if (accessChecked && accessDenied) {
        return <Redirect to="/admin/dashboard" />;
    }

    return (
        <div>
            <SideNav />

            <Grid container className={classes.rootContainer}>
                <Grid item xs={12} style={{ padding: 8 }}>
                    <h1 style={{ marginBlockEnd: 0 }}>{get('selfDesignPricing')}</h1>
                    <hr />
                </Grid>

                <Grid item xs={12}>
                    <Grid container>
                        <Grid item xs={6} className={classes.filterGrid}>
                            <InputLabel shrink>{get('distributorGroup')}</InputLabel>
                            <Select
                                value={selectedDistrGroup}
                                onChange={(e) => {
                                    setSelectedDistrGroup(e.target.value);
                                    setPricingData([]);
                                }}
                                style={{ width: '100%' }}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>
                                    <em>{get('selectDistributorGroup')}</em>
                                </MenuItem>
                                {distrGroupOptions}
                            </Select>
                        </Grid>
                        <Grid item xs={6} className={classes.filterGrid}>
                            <InputLabel shrink>{get('currency')}</InputLabel>
                            <Select
                                value={selectedCurrency}
                                onChange={(e) => {
                                    setSelectedCurrency(e.target.value);
                                    setPricingData([]);
                                }}
                                style={{ width: '100%' }}
                            >
                                {currencyOptions}
                            </Select>
                            <FormHelperText>{get('currencyHelperText')}</FormHelperText>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} style={{ padding: 8, marginTop: 16 }}>
                    {selectedDistrGroup && selectedCurrency && (
                        <div
                            style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 8px', marginBottom: 16 }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={selectedCurrency === savedCurrency || savingCurrency}
                                onClick={handleSaveCurrency}
                            >
                                {get('saveCurrency')}
                            </Button>
                        </div>
                    )}
                    {loading ? (
                        <Grid container style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
                            <CircularProgress style={{ color: 'rgb(6,26,46)' }} />
                        </Grid>
                    ) : selectedDistrGroup && selectedCurrency ? (
                        <MUIDataTable
                            title={get('selfDesignPricingList')}
                            data={pricingData}
                            columns={columns}
                            options={{
                                download: false,
                                filterType: 'dropdown',
                                responsive: 'scroll',
                                selectableRows: false,
                                print: true,
                                search: true,
                                viewColumns: true,
                                rowsPerPage: 30,
                                rowsPerPageOptions: [15, 30, 50],
                            }}
                        />
                    ) : (
                        <p style={{ textAlign: 'center', color: '#666', marginTop: 40 }}>{get('selectBothToView')}</p>
                    )}
                </Grid>
            </Grid>

            {/* Edit Modal */}
            <Modal
                className="modal"
                modal="EditPrice"
                title={get('editDistributorCost')}
                open={editModalOpen}
                modalError={editModalError}
                handleClose={() => setEditModalOpen(false)}
                handleDataSave={handleSave}
                submitButton={true}
                submitButtonLabel={get(':const.save')}
            >
                {editRow && (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <p style={{ margin: 0, color: '#666' }}>
                                Fields marked with an asterisk (<span style={{ color: 'red' }}>*</span>) are mandatory
                            </p>
                        </Grid>
                        <Grid item xs={6}>
                            <InputLabel shrink>{get('productCode')}</InputLabel>
                            <TextField
                                value={editRow.product_code}
                                fullWidth
                                disabled
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <InputLabel shrink>{get('productName')}</InputLabel>
                            <TextField
                                value={editRow.product_name}
                                fullWidth
                                disabled
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <InputLabel shrink>{get('distributorGroup')}</InputLabel>
                            <TextField
                                value={selectedDistrGroupName}
                                fullWidth
                                disabled
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <InputLabel shrink>{get('currency')}</InputLabel>
                            <TextField value={selectedCurrency} fullWidth disabled InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12}>
                            <p style={{ marginBottom: 4 }}>
                                {get('distributorCost')} <span style={{ color: 'red' }}>*</span>
                            </p>
                            <TextField
                                type="number"
                                value={editCost}
                                onChange={(e) => {
                                    setEditCost(e.target.value);
                                    setEditModalError('');
                                }}
                                fullWidth
                                placeholder={get('distributorCost')}
                                inputProps={{
                                    min: 0,
                                    step: '0.01',
                                }}
                            />
                        </Grid>
                    </Grid>
                )}
            </Modal>
        </div>
    );
};

export default withRouter(SelfDesignPricing);
